import React, { type FC, useCallback } from 'react';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { useDispatch, useSelector } from '../../../store';
import { useBiLogger } from '../../Provider/BiLogger/BiLoggerProvider';
import { useFedopsLogger } from '../../Provider/Fedops/FedopsProvider';
import {
  selectGooglePayBtnColor,
  selectGooglePayButtonAvailable,
  selectGooglePayCountryCode,
  selectGooglePayDto,
  selectGooglePayOptions,
  selectGooglePayPaymentData,
  selectSessionId,
  selectTopProps,
} from '../../../selectors';
import { type PaymentsButtonPlace } from '../../../types/PaymentsButtonViewConfig';
import {
  setGooglePayButtonVisibility,
  setGooglePayPaymentDataShippingMethods,
} from '../../../actions/googlePay/common';
import { logGeneralError } from '../../../utils/sentry';
import { errorToString } from '../../../utils/sentry/errorToString';
import { amountToLong } from '../../../utils/amountToLong';
import {
  paymentModalClosed,
  spbButtonSuccessInitializationCheckout,
  spbErrorDuringStartInitializationCheckout,
} from '../../../utils/bi/events/biEvents';
import { spbAnyErrorDuringGpayFlow } from '../../../utils/bi/events/googlePay';
import { isGooglePayLoaded } from './utils/paymentsClient';
import { getPaymentDataRequest } from './utils/getPaymentDataRequest';
import { getInitialTransactionInfo } from './utils/getInitialPaymentData';
import { getIsNeedPassOnPaymentDataChanged } from './utils/getIsNeedPassOnPaymentDataChanged';
import { createOnClick } from './callbacks/createOnClick';
import { createOnReadyToPayChange } from './callbacks/createOnReadyToPayChange';
import { createOnShippingMethodSelected } from './callbacks/createOnShippingMethodSelected';
import { createOnShippingContactSelected } from './callbacks/createOnShippingContactSelected';
import { createOnPaymentAuthorized } from './callbacks/createOnPaymentAuthorized';
import { type GooglePayButtonConfig } from './types';
import { GooglePayView } from './view';

interface GooglePayControllerProps {
  place: PaymentsButtonPlace;
}

/**
 * @summary GooglePay button controller which aggregate props for GPay button
 * @see Props changes of which will trigger full delete and re-mount GPay button - "@google-pay/button-react": "^3.0.5",
 * @link https://github.com/google-pay/google-pay-button/blob/main/src/lib/button-manager.ts#L398
 */
export const GooglePayController: FC<GooglePayControllerProps> = ({ place }) => {
  const dispatch = useDispatch();
  const biLogger = useBiLogger();
  const fedopsLogger = useFedopsLogger();
  const googlePayPM = useSelector(selectGooglePayDto);
  const googlePayOptions = useSelector(selectGooglePayOptions);
  // TODO refactor: Checked already in selectAvailableMethods
  const isGooglePayButtonAvailable = useSelector(selectGooglePayButtonAvailable);
  const countryCode = useSelector(selectGooglePayCountryCode);
  const color = useSelector(selectGooglePayBtnColor);
  const topProps = useSelector(selectTopProps);
  const sessionId = useSelector(selectSessionId);
  const { shippingMethods } = useSelector(selectGooglePayPaymentData);

  const paymentRequest = getPaymentDataRequest({
    topProps,
    countryCode,
    sessionId,
    googlePayPM,
    googlePayOptions,
    shippingMethods,
  });
  const { transactionInfo } = paymentRequest;
  const onClick = createOnClick({ topProps, biLogger });
  const onPaymentAuthorized = createOnPaymentAuthorized({ transactionInfo, biLogger, fedopsLogger, dispatch });
  const onReadyToPayChange = createOnReadyToPayChange({ place, biLogger, dispatch });
  const onShippingMethodSelected = createOnShippingMethodSelected({ transactionInfo, biLogger, dispatch });
  const onShippingContactSelected = createOnShippingContactSelected({
    transactionInfo,
    biLogger,
    dispatch,
    onShippingMethodSelected,
  });
  const isNeedPassOnPaymentDataChanged = getIsNeedPassOnPaymentDataChanged(paymentRequest.callbackIntents);

  const onPaymentDataChanged: GooglePayButtonConfig['onPaymentDataChanged'] = async (intermediatePaymentData) => {
    if (intermediatePaymentData.callbackTrigger === 'INITIALIZE') {
      // Will be triggered only for physical product (when [requestShipping: true])
      const initialTransactionInfo = await getInitialTransactionInfo({
        paymentAmount: topProps.paymentAmount,
        paymentBreakdown: topProps.paymentBreakdown,
        currency: topProps.currency,
        requestShipping: topProps.requestShipping,
        countryCode,
        sessionId,
        getInitialPaymentData: topProps.getInitialPaymentData,
        biLogger,
      });

      if (initialTransactionInfo.error) {
        biLogger.report(spbErrorDuringStartInitializationCheckout, {
          amount: amountToLong(topProps.paymentAmount),
          currency: topProps.currency,
          errorReason: JSON.stringify(initialTransactionInfo.error),
          paymentProvider: PaymentProvider.WixPayUS,
          paymentmethod: PaymentMethod.GooglePay,
          isPartner: undefined as any, // TODO[bi]
          orderSnapshotId: undefined as any, // TODO[bi]
          transactionId: undefined as any, // TODO[bi]
          productId: undefined as any, // TODO[bi]
          cartId: undefined as any, // TODO[bi]
          errorDesc: undefined as any, // TODO[bi]
        });
        return initialTransactionInfo;
      }

      if (!initialTransactionInfo.error) {
        biLogger.report(spbButtonSuccessInitializationCheckout, {
          paymentProvider: PaymentProvider.WixPayUS,
          paymentmethod: PaymentMethod.GooglePay,
          // @ts-expect-error default value override
          requestShipping: topProps.requestShipping,
        });
      }

      // google pass shippingAddress previously saved by user in google account
      if (intermediatePaymentData.shippingAddress) {
        return onShippingContactSelected(intermediatePaymentData.shippingAddress);
      }

      return initialTransactionInfo;
    }

    if (intermediatePaymentData.callbackTrigger === 'SHIPPING_OPTION') {
      return onShippingMethodSelected(intermediatePaymentData.shippingOptionData);
    }

    if (intermediatePaymentData.callbackTrigger === 'SHIPPING_ADDRESS') {
      return onShippingContactSelected(intermediatePaymentData.shippingAddress);
    }

    return {};
  };
  const onCancel = () => {
    biLogger.report(paymentModalClosed, {
      paymentProvider: PaymentProvider.WixPayUS,
      paymentmethod: PaymentMethod.GooglePay,
      cartId: topProps.meta.cartId,
      isPartner: undefined as any, // TODO[bi]
      orderSnapshotId: undefined as any, // TODO[bi]
      transactionId: undefined as any, // TODO[bi]
      currency: undefined as any, // TODO[bi]
      amount: undefined as any, // TODO[bi]
      externalOrderId: undefined as any, // TODO[bi]
      checkoutType: undefined as any, // TODO[bi]
      pageType: undefined as any, // TODO[bi]
    });

    dispatch(setGooglePayPaymentDataShippingMethods(topProps.shippingMethods)); // Reset shipping methods to initial state
    topProps.onCancel && topProps.onCancel();
  };

  const onError = useCallback(
    (error: unknown) => {
      /**
       * Errors can occur either during SDK loading or while collecting payment
       * options in the Google Pay payment sheet. If the SDK fails to load, Google
       * does not update `isButtonVisible` in `onReadyToPayChange`, and there is no
       * dedicated callback for SDK-loading errors.
       * In this case, check for `PaymentsClient` on `window` and hide the button.
       */
      if (!isGooglePayLoaded()) {
        dispatch(setGooglePayButtonVisibility('hidden'));
      }

      logGeneralError({
        message: '[GooglePay] onError',
        payload: error,
      });
      biLogger.report(spbAnyErrorDuringGpayFlow, {
        errorReason: errorToString(error),
        paymentProvider: PaymentProvider.WixPayUS,
        paymentmethod: PaymentMethod.GooglePay,
        currency: undefined as any, // TODO[bi]
        amount: undefined as any, // TODO[bi]
        isPartner: undefined as any, // TODO[bi]
        cartId: undefined as any, // TODO[bi]
        productId: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
      });
    },
    [biLogger, dispatch],
  );

  return (
    isGooglePayButtonAvailable && (
      <GooglePayView
        locale={topProps.locale}
        height={topProps.buttonStyle?.height}
        shape={topProps.buttonStyle?.shape}
        color={color}
        buttonType="plain"
        paymentRequest={paymentRequest}
        onClick={onClick}
        onReadyToPayChange={onReadyToPayChange}
        onCancel={onCancel}
        onPaymentAuthorized={onPaymentAuthorized}
        onPaymentDataChanged={isNeedPassOnPaymentDataChanged && onPaymentDataChanged}
        onError={onError}
      />
    )
  );
};
